import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { StepIndicator } from "../components/StepIndicator";
import { SelectionCard } from "../components/SelectionCard";
import { Button } from "../components/ui/button";
import {
  USER_TYPES,
  SECTORS,
  PROBLEMS,
  GOVERNANCE_SCOPES,
} from "../lib/content";

const STEPS = [
  { id: "user_type", label: "Role", key: "userTypeId" },
  { id: "sector", label: "Sector", key: "sectorId" },
  { id: "problem", label: "Coordination problem", key: "problemId" },
  { id: "scope", label: "Governance scope", key: "scopeId" },
];

const STORAGE_KEY = "cierge.draft.in-progress";

const Stage = ({ index, total, eyebrow, headline, prompt }) => (
  <div>
    <p
      data-testid="stage-eyebrow"
      className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground"
    >
      Step {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · {eyebrow}
    </p>
    <h2
      data-testid="stage-headline"
      className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-foreground sm:text-5xl"
    >
      {headline}
    </h2>
    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
      {prompt}
    </p>
  </div>
);

export const Explore = () => {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [selection, setSelection] = useState({
    userTypeId: null,
    sectorId: null,
    problemId: null,
    scopeId: null,
  });

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) setSelection(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  }, [selection]);

  const currentStep = STEPS[stepIndex];
  const currentValue = selection[currentStep.key];
  const canAdvance = !!currentValue;

  const handleSelect = (id) => {
    setSelection((prev) => ({ ...prev, [currentStep.key]: id }));
  };

  const handleNext = () => {
    if (!canAdvance) return;
    if (stepIndex < STEPS.length - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
      navigate("/explore/output");
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const optionsByStep = useMemo(
    () => ({
      user_type: USER_TYPES.map((u) => ({
        id: u.id,
        label: u.label,
        description: u.summary,
      })),
      sector: SECTORS.map((s) => ({
        id: s.id,
        label: s.label,
        description: s.blurb,
      })),
      problem: PROBLEMS.map((p) => ({
        id: p.id,
        label: p.label,
        description: p.blurb,
      })),
      scope: GOVERNANCE_SCOPES.map((g) => ({
        id: g.id,
        label: g.label,
        description: g.blurb,
      })),
    }),
    [],
  );

  const stageMeta = {
    user_type: {
      eyebrow: "Role",
      headline: "Who is shaping this use case?",
      prompt:
        "Your role narrows the recommended next step at the end of the flow. None of this requires you to disclose anything about your organisation.",
    },
    sector: {
      eyebrow: "Sector",
      headline: "Which initial sector applies?",
      prompt:
        "Sector frames the synthetic case study returned at the end. v1 covers four operational domains.",
    },
    problem: {
      eyebrow: "Coordination problem",
      headline: "What kind of coordination is failing?",
      prompt:
        "Pick the closest type of coordination problem. The output will frame the governance contract for that class of problem.",
    },
    scope: {
      eyebrow: "Governance scope",
      headline: "What specifically needs governing?",
      prompt:
        "Choose one governance primitive to anchor the use case. KATLAS supports all four; for v1 you anchor on one.",
    },
  };

  const meta = stageMeta[currentStep.id];
  const options = optionsByStep[currentStep.id];

  return (
    <div data-testid="explore-page" className="bg-white">
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-12 lg:px-10">
          <StepIndicator
            steps={[...STEPS, { id: "output", label: "Tailored output" }]}
            currentIndex={stepIndex}
          />
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-16 md:py-20 lg:px-10">
          <Stage
            index={stepIndex}
            total={STEPS.length}
            eyebrow={meta.eyebrow}
            headline={meta.headline}
            prompt={meta.prompt}
          />

          <div
            key={currentStep.id}
            data-testid={`stage-${currentStep.id}`}
            className="mt-12 grid gap-5 motion-safe:animate-[fadein_0.35s_ease-out] sm:grid-cols-2 lg:grid-cols-2"
          >
            {options.map((opt) => (
              <SelectionCard
                key={opt.id}
                id={opt.id}
                label={opt.label}
                description={opt.description}
                selected={currentValue === opt.id}
                onSelect={handleSelect}
                testIdPrefix={`option-${currentStep.id}`}
              />
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
            <Button
              type="button"
              variant="ghost"
              onClick={handleBack}
              disabled={stepIndex === 0}
              data-testid="explore-back"
              className="rounded-none font-mono text-xs uppercase tracking-[0.18em]"
            >
              ← Back
            </Button>
            <div className="flex items-center gap-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {canAdvance ? "Selection captured" : "Make a selection to continue"}
              </span>
              <Button
                type="button"
                onClick={handleNext}
                disabled={!canAdvance}
                data-testid="explore-next"
                className="rounded-none px-6 font-mono text-xs uppercase tracking-[0.18em]"
              >
                {stepIndex === STEPS.length - 1
                  ? "Generate tailored output →"
                  : "Continue →"}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Explore;
